import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Section } from "@/components/Section";
import { identity } from "@/data/portfolio";

const CONTACT_EMAIL = "remotedevkaran@gmail.com";

const capabilities = [
  { name: "LLM & RAG Systems", desc: "Design and implementation of retrieval-augmented applications, knowledge systems, retrieval pipelines, vector search, grounding strategies, and production integrations." },
  { name: "AI Agents & Tool Calling", desc: "Agentic workflows that interact with APIs, internal systems, business tools, databases, and controlled actions." },
  { name: "AI Automation", desc: "Engineering workflows that combine models, deterministic software, business logic, integrations, and human approval where appropriate." },
  { name: "Backend & API Architecture", desc: "Production backend services, APIs, authentication, integrations, asynchronous workflows, data pipelines, and supporting infrastructure." },
  { name: "AI Integration", desc: "Adding AI capabilities to existing SaaS products and internal systems without unnecessarily rebuilding the underlying product." },
  { name: "Evaluation & Reliability", desc: "Testing, observability, failure analysis, guardrails, retrieval evaluation, regression testing, and production reliability improvements." },
];

const engagements = [
  {
    name: "Defined Project",
    desc: "For companies with a specific system, feature, integration, prototype-to-production requirement, or engineering problem.",
    items: ["Discovery", "Scope", "Architecture", "Milestones", "Development", "Testing", "Deployment", "Documentation & Handover"],
  },
  {
    name: "Fractional Engineering",
    desc: "For companies that need experienced Applied AI engineering capacity on an ongoing basis without immediately adding another permanent senior engineer.",
    items: ["Agreed weekly/monthly capacity", "Ongoing implementation", "Architecture and technical decisions", "Integrations", "Production troubleshooting", "Iteration and improvement"],
  },
  {
    name: "Build + Ongoing Support",
    desc: "For companies that want one person to take ownership of the initial implementation and remain involved after launch.",
    items: ["Build", "Production Launch", "Monitoring / Improvements", "Ongoing Engineering Support"],
  },
];

const whenToTalk = [
  "You are actively hiring for AI engineering capability but have an immediate project that cannot wait for a long hiring cycle.",
  "Your existing engineering team needs someone to own the AI/RAG/agent layer.",
  "You have an AI prototype that needs to become a reliable production system.",
  "You need senior engineering capacity for a defined initiative without committing immediately to permanent headcount.",
  "You need ongoing technical ownership after the first version ships.",
  "You need AI capabilities integrated into an existing SaaS/backend architecture rather than building another isolated demo.",
];

const process = [
  { name: "Discovery", desc: "Understand the business problem, current architecture, constraints, expected outcome, timeline, and ownership boundaries." },
  { name: "Scope", desc: "Define deliverables, technical responsibilities, milestones, dependencies, acceptance criteria, and engagement structure." },
  { name: "NDA & Commercial Agreement", desc: "Establish confidentiality, IP ownership, payment terms, responsibilities, and other contractual requirements before sensitive work begins." },
  { name: "Architecture", desc: "Define the implementation approach, integration boundaries, security requirements, infrastructure, and delivery plan." },
  { name: "Build", desc: "Execute against agreed milestones with transparent progress and documented technical decisions." },
  { name: "Validate", desc: "Testing, evaluation, failure analysis, security considerations, and production-readiness checks." },
  { name: "Deploy & Handover", desc: "Deployment, documentation, knowledge transfer, source/code ownership according to the agreement, and operational handover." },
  { name: "Continue if Needed", desc: "Transition into ongoing fractional engineering/support where useful." },
];

const environment = [
  { name: "NDA-Ready", desc: "Confidential engagements can operate under an appropriate NDA before sensitive technical information is exchanged." },
  { name: "Defined IP Ownership", desc: "Project IP, source-code ownership, pre-existing IP, deliverables, and licensing boundaries are explicitly defined in the commercial agreement." },
  { name: "Least-Privilege Access", desc: "Only the access required to perform agreed engineering work is requested." },
  { name: "Environment Separation", desc: "Client credentials, infrastructure access, source repositories, datasets, and environments remain appropriately separated." },
  { name: "Credential Security", desc: "Client secrets are not placed unnecessarily in personal systems or documentation." },
  { name: "Documented Handover", desc: "Architecture, deployment information, relevant operational knowledge, and implementation documentation are transferred as part of project completion." },
  { name: "Access Revocation", desc: "Project-specific access is revocable when an engagement or responsibility concludes." },
];

const ownership = [
  "8+ years across production software engineering and technical leadership",
  "Recent specialization in production Applied AI systems",
  "Experience spanning AI, backend architecture, automation, integrations, and deployment",
  "Direct communication with the engineer responsible for implementation",
  "Ability to work from architecture through production",
  "Experience collaborating remotely with international teams",
  "Project-based, fractional, and longer-term engagement options",
];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function AiEngineering() {
  return (
    <main data-testid="ai-engineering-page" className="pt-16">
      <div className="scanlines relative overflow-hidden border-b border-white/10 px-6 py-24 md:px-12 md:py-36 lg:px-20">
        <div className="grid-bg absolute inset-0 opacity-60" aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 max-w-5xl"
        >
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"//"} India | International Remote · US/EU time-zone overlap
          </p>
          <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter text-white md:text-7xl">
            Fractional Applied<br />
            <span className="text-outline">AI Engineering</span>
          </h1>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
            Production AI systems without requiring another permanent engineering hire. I work with
            startups and engineering teams on defined Applied AI projects and longer-term fractional
            engagements — from architecture and implementation through production deployment,
            documentation, and ongoing engineering support.
          </p>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            Karan Sahu · 8+ Years Production Engineering
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="ai-eng-discuss-btn"
              className="group flex items-center gap-3 bg-cyan-400 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
            >
              Discuss a Project
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/"
              data-testid="ai-eng-portfolio-link"
              className="flex items-center gap-3 border border-white/20 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:border-cyan-400 hover:text-cyan-400"
            >
              View Engineering Portfolio
            </Link>
          </div>
        </motion.div>
      </div>

      <Section id="capabilities" index="A1" title="Capabilities">
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((c, i) => (
            <FadeIn key={c.name} delay={i * 0.04} className="group bg-[#050505] p-8 transition-colors duration-500 hover:bg-[#0A0A0A]">
              <h3 className="font-display text-lg font-bold text-white transition-colors duration-300 group-hover:text-cyan-400">
                {c.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-500">{c.desc}</p>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section id="engagements" index="A2" title="Engagements">
        <div className="grid gap-8 lg:grid-cols-3">
          {engagements.map((e, i) => (
            <FadeIn key={e.name} delay={i * 0.06} className="flex flex-col border border-white/10 bg-[#0A0A0A] p-8 transition-colors duration-500 hover:border-cyan-400/50 md:p-10">
              <span className="font-mono text-sm tracking-[0.3em] text-cyan-400">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-xl font-bold uppercase text-white md:text-2xl">{e.name}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-zinc-400">{e.desc}</p>
              <div className="mt-6 flex flex-wrap gap-1.5 border-t border-white/10 pt-5">
                {e.items.map((item) => (
                  <span key={item} className="bg-white/5 px-2 py-1 font-mono text-[10px] text-zinc-500">{item}</span>
                ))}
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn className="mt-16 border border-white/10 bg-[#0A0A0A] p-8 md:p-10">
          <h3 className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{"//"} When to talk</h3>
          <ul className="grid gap-x-10 gap-y-3 md:grid-cols-2">
            {whenToTalk.map((w, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                <span className="mt-2 h-px w-4 shrink-0 bg-cyan-400/50" />
                {w}
              </li>
            ))}
          </ul>
        </FadeIn>
      </Section>

      <Section id="process" index="A3" title="Structured Like Engineering, Not Freelancing">
        <div className="border-b border-white/10">
          {process.map((p, i) => (
            <FadeIn key={p.name} className="group grid gap-2 border-t border-white/10 py-6 transition-colors duration-300 hover:bg-white/[0.02] md:grid-cols-[80px_280px_1fr] md:gap-8">
              <span className="font-mono text-sm text-cyan-400">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-display text-base font-bold text-white transition-colors duration-300 group-hover:text-cyan-400 md:text-lg">
                {p.name}
              </h3>
              <p className="text-sm leading-relaxed text-zinc-400">{p.desc}</p>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section id="environment" index="A4" title="Built Around Your Environment">
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {environment.map((e, i) => (
            <FadeIn key={e.name} delay={i * 0.03} className="bg-[#050505] p-8 transition-colors duration-500 hover:bg-[#0A0A0A]">
              <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">{e.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-500">{e.desc}</p>
            </FadeIn>
          ))}
          <FadeIn delay={0.2} className="bg-[#050505] p-8">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Founder Perspective</h3>
            <p className="mt-3 text-sm leading-relaxed text-zinc-500">
              Alongside engineering engagements, I am building Prefiler through Prefiler Labs Private Limited.
              Technical decisions need to make sense not only in isolation, but also in terms of product
              constraints, operating cost, maintainability, security, and long-term ownership.
            </p>
          </FadeIn>
        </div>
      </Section>

      <Section id="ownership" index="A5" title="Senior Engineering Ownership">
        <div className="grid gap-16 lg:grid-cols-2">
          <FadeIn>
            <ul className="space-y-3">
              {ownership.map((o, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-400 md:text-base">
                  <span className="mt-2.5 h-px w-5 shrink-0 bg-cyan-400/50" />
                  {o}
                </li>
              ))}
            </ul>
            <Link
              to="/"
              data-testid="ai-eng-experience-link"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 transition-colors duration-300 hover:text-white"
            >
              View full experience and selected systems <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </FadeIn>
          <FadeIn delay={0.1} className="scanlines border border-white/15 bg-[#0A0A0A] p-8 md:p-10">
            <h3 className="font-display text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
              Have an AI Engineering problem to solve?
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">
              If you're building an AI-enabled product, adding AI capabilities to an existing system, or need
              experienced engineering ownership for a defined initiative, we can start with a short technical
              conversation.
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">
              India | International Remote · US/EU time-zone overlap
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="ai-eng-contact-btn"
                className="group flex items-center gap-2 bg-cyan-400 px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
              >
                <Mail className="h-4 w-4" /> Discuss a Project
              </a>
              <a
                href={identity.links.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ai-eng-linkedin-link"
                className="flex items-center gap-2 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:border-cyan-400 hover:text-cyan-400"
              >
                Connect on LinkedIn
              </a>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
